import type { DiscoverFilterOptions } from "~/react/types/discover"

const t = (key: string) => window.i18n.t(key)

export function joinList(values?: string[]): string {
  if (!values || values.length === 0) return ""
  return values.join(", ")
}

export function formatIncludeExclude(
  included?: string[],
  excluded?: string[],
): string {
  const parts: string[] = []

  if (included && included.length > 0) {
    parts.push(included.join(", "))
  }

  if (excluded && excluded.length > 0) {
    const excludePrefix = t("js.back.discover.compact.exclude_prefix")
    parts.push(`${excludePrefix} ${excluded.join(", ")}`)
  }

  return parts.join(", ")
}

export function mapCompanyTypeLabels(
  values: string[] | undefined,
  companyTypes: DiscoverFilterOptions["company_types"],
): string[] {
  if (!values || values.length === 0) return []

  const labelByValue = new Map(
    companyTypes.map((type) => [type.value, type.label]),
  )

  return values.map((value) => labelByValue.get(value) || value)
}

export function mapIndustryLabels(
  values: string[] | undefined,
  industries: DiscoverFilterOptions["industries"],
): string[] {
  if (!values || values.length === 0) return []

  const nameById = new Map(
    industries.map((industry) => [industry.id, industry.name]),
  )

  return values.map((id) => nameById.get(id) || id)
}

export function mapTechnologyLabels(
  values: string[] | undefined,
  technologies: DiscoverFilterOptions["technologies"],
): string[] {
  if (!values || values.length === 0) return []

  const allTechnologies = Object.values(technologies).flat()
  const displayNameBySlug = new Map(
    allTechnologies.map((tech) => [tech.slug, tech.display_name]),
  )

  return values.map((slug) => displayNameBySlug.get(slug) || slug)
}

export function formatFundingSeries(seriesValues: string[]): string {
  const VALID_SERIES = [
    { value: "pre_seed", key: "pre_seed" },
    { value: "seed", key: "seed" },
    { value: "pre_series_a", key: "pre_series_a" },
    { value: "series_a", key: "series_a" },
    { value: "pre_series_b", key: "pre_series_b" },
    { value: "series_b", key: "series_b" },
    { value: "pre_series_c", key: "pre_series_c" },
    { value: "series_c+", key: "series_c_plus" },
    { value: "other", key: "other" },
  ]

  const seriesLabels = seriesValues.map((value) => {
    const series = VALID_SERIES.find((s) => s.value === value)
    if (series) {
      return t(`js.back.discover.filters.funding.series.${series.key}`)
    }
    return value
  })

  return seriesLabels.join(", ")
}

export function formatFundingAmount(amountFrom?: number, amountTo?: number): string {
  const formatNumber = (num: number): string => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  }

  if (amountFrom && amountTo) {
    return `$${formatNumber(amountFrom)} – $${formatNumber(amountTo)}`
  } else if (amountFrom) {
    return `$${formatNumber(amountFrom)}+`
  } else if (amountTo) {
    return `≤$${formatNumber(amountTo)}`
  }
  return ""
}

export function formatFundingDate(
  dateValue?: string,
  dateFrom?: number,
  dateTo?: number,
): string {
  if (dateValue && dateValue !== "custom") {
    return t(`js.back.discover.filters.funding.date_options.${dateValue}`)
  } else if (dateFrom && dateTo) {
    const fromLabel = t("js.back.discover.filters.from")
    const toLabel = t("js.back.discover.filters.to")
    return `${fromLabel} ${dateFrom} – ${toLabel} ${dateTo}`
  } else if (dateFrom) {
    const fromLabel = t("js.back.discover.filters.from")
    return `${fromLabel} ${dateFrom}`
  } else if (dateTo) {
    const toLabel = t("js.back.discover.filters.to")
    return `${toLabel} ${dateTo}`
  }
  return ""
}

